import type { SearchFilters } from "@/lib/api/types";
import { formatEntityForms } from "@/lib/search/entity-variants";

const DEFAULT_FILTER_VALUES: Record<string, unknown> = {
  jt_filter_type: "c",
  distance: "25mi",
  company_tag_filter_type: "cp",
  funding_filter_type: "cp",
  investor_filter_type: "cp",
  industry_filter_type: "cp",
  funding_scope: "any",
  min_degree: "none",
  certification_status: "any",
  pf_filter_type: "any",
  exclude_org_companies: false,
  exclude_org_profiles: true,
  exclude_dnc_companies: false,
  exclude_dnc_countries: false,
};

function isEmptyValue(value: unknown): boolean {
  if (value === undefined || value === null || value === false || value === "") {
    return true;
  }

  if (Array.isArray(value)) {
    return value.length === 0;
  }

  if (typeof value === "object") {
    if ("companies" in (value as Record<string, unknown>)) {
      const companies = (value as { companies?: unknown[] }).companies;
      return !companies || companies.length === 0;
    }

    return Object.values(value as Record<string, unknown>).every((nested) => isEmptyValue(nested));
  }

  return false;
}

function isDefaultFilterValue(
  key: string,
  value: unknown
): boolean {
  return key in DEFAULT_FILTER_VALUES && DEFAULT_FILTER_VALUES[key] === value;
}

export function countAppliedFilters(filters: SearchFilters | Record<string, unknown>): number {
  return Object.entries(filters).reduce(
    (count, [key, value]) => {
      // Exclusions are on by default, so `false` (turned off) is the
      // applied state; the generic empty check would swallow it.
      if (key === "exclude_org_profiles") {
        return value === false ? count + 1 : count;
      }

      if (isEmptyValue(value)) {
        return count;
      }

      if (isDefaultFilterValue(key, value)) {
        return count;
      }

      return count + 1;
    },
    0
  );
}

export function getVisiblePageNumbers(totalPages: number, currentPage: number, maxVisible = 4): number[] {
  let start = Math.max(1, currentPage - 1);
  const end = Math.min(totalPages, start + maxVisible - 1);
  if (end - start < maxVisible - 1) {
    start = Math.max(1, end - maxVisible + 1);
  }

  const pages: number[] = [];
  for (let page = start; page <= end; page += 1) {
    pages.push(page);
  }
  return pages;
}

export function shouldShowCoverageLoading({
  coverageCount,
  coverageFetching,
}: {
  coverageCount?: number;
  coverageFetching: boolean;
}): boolean {
  return coverageFetching && coverageCount === undefined;
}

/** Fallback summary when `SavedSearch.description` is null — matches talent-search empty state. */
export function describeSearchFilters(filters: SearchFilters | Record<string, unknown>): string {
  const f = filters as SearchFilters;
  if (f.full_text_search?.trim()) {
    return f.full_text_search.trim();
  }
  if (f.job_titles?.length) {
    return `Job titles: ${f.job_titles.map(formatEntityForms).filter(Boolean).join(", ")}`;
  }
  if (f.skills?.length) {
    return `Skills: ${f.skills.map(formatEntityForms).filter(Boolean).join(", ")}`;
  }
  if (f.locations?.length) {
    return `Locations: ${f.locations.map((item) => item.region || item.country || item.locality).filter(Boolean).join(", ")}`;
  }
  return "Saved filter search";
}
